/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import * as components from "../components/index.js";
import { TeralError } from "./teralerror.js";

/**
 * Error response when the request conflicts with the current state of the resource. This occurs when:
 *
 * @remarks
 * - Attempting to create a resource that already exists
 * - Modifying a resource that has been changed by another operation
 * - Violating unique constraints or business rules
 *
 * To resolve this error, check the current state of the resource and adjust your request accordingly.
 */
export type ConflictErrorResponseData = {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;
};

/**
 * Error response when the request conflicts with the current state of the resource. This occurs when:
 *
 * @remarks
 * - Attempting to create a resource that already exists
 * - Modifying a resource that has been changed by another operation
 * - Violating unique constraints or business rules
 *
 * To resolve this error, check the current state of the resource and adjust your request accordingly.
 */
export class ConflictErrorResponse extends TeralError {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;

  /** The original data that was passed to this error instance. */
  data$: ConflictErrorResponseData;

  constructor(
    err: ConflictErrorResponseData,
    httpMeta: { response: Response; request: Request; body: string },
  ) {
    const message = "message" in err && typeof err.message === "string"
      ? err.message
      : `API error occurred: ${JSON.stringify(err)}`;
    super(message, httpMeta);
    this.data$ = err;
    this.meta = err.meta;
    this.error = err.error;

    this.name = "ConflictErrorResponse";
  }
}

/** @internal */
export const ConflictErrorResponse$inboundSchema: z.ZodType<
  ConflictErrorResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  meta: components.Meta$inboundSchema,
  error: components.BaseError$inboundSchema,
  request$: z.instanceof(Request),
  response$: z.instanceof(Response),
  body$: z.string(),
})
  .transform((v) => {
    return new ConflictErrorResponse(v, {
      request: v.request$,
      response: v.response$,
      body: v.body$,
    });
  });
